import React, { useState, useCallback, useEffect, useRef } from 'react';
import { Box, Drawer, List, ListItem, ListItemText, ListItemIcon, IconButton, Typography, Button } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SavedItemsList from '../Dashboard/UserDashboard/SavedItemsList';
import Map from '../Map/Map';
import Header from '../Header/Header';
import RightSidebar from './RightSidebar';
import BottomMenu from './BottomMenu';
import QrCodeScannerIcon from '@mui/icons-material/QrCodeScanner';
import PlaylistAddIcon from '@mui/icons-material/PlaylistAdd';
import PeopleAltIcon from '@mui/icons-material/PeopleAlt';
import DashboardIcon from '@mui/icons-material/Dashboard';
import LoginIcon from '@mui/icons-material/Login';
import { useLanguage } from '../../contexts/LanguageContext';
import { useAuth } from '../../hooks/useAuth';
import useTTS from '../../hooks/useTTS';
import { TokenService } from '../../utils/TokenService';
import { formatEventForTTS } from '../../utils/ttsFormatter';
import menuItems from '../../utils/menuConfig';
import { useNavigate } from 'react-router-dom';
import CategorySelectionModal from '../InputPages/CategorySelectionModal';

const DesktopLayout = ({
  coordinates,
  setCoordinates,
  bounds,
  setBounds,
  childClicked,
  setChildClicked,
  handleMarkerClick,
  handleLocationClick,
  currentView,
  setCurrentView,
  type,
  setType,
  startDate,
  setStartDate,
  endDate,
  setEndDate,
  followList,
  setFollowList,
  userLocation,
  feedLat,
  feedLng,
  feedRadiusKm,
  onSearch,
  searchQuery, // NEW: Search query to pass to SmartEventList
  hasSearched,
  source,              // NEW: Which collections to search
  excludeCategories,   // NEW: Categories to exclude
  categoryFilter,      // NEW: Category filter from tab config (resolved, for SmartEventList)
  uiCategoryFilter,    // NEW: Raw dropdown state (for Header display)
  setUiCategoryFilter, // NEW: Setter for Header dropdown
  selectedCreator,
  setSelectedCreator,
  creatorProfile,
  onCreatorClick,
}) => {
  const { language } = useLanguage();
  const { isAuthenticated, logout } = useAuth();
  const navigate = useNavigate();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [categoryModalOpen, setCategoryModalOpen] = useState(false);
  const [showSaved, setShowSaved] = useState(false);
  const [savedEverOpened, setSavedEverOpened] = useState(false);
  const [savedRefreshKey, setSavedRefreshKey] = useState(0);

  // Followed creators list state
  const [savedTab, setSavedTab] = useState('items'); // 'items' or 'following'
  const [followedCreators, setFollowedCreators] = useState([]);
  const [followedCreatorsLoading, setFollowedCreatorsLoading] = useState(false);

  const fetchFollowedCreators = useCallback(async () => {
    if (!isAuthenticated) return;
    setFollowedCreatorsLoading(true);
    try {
      const activeToken = TokenService.getAccessToken();
      const res = await fetch(`${process.env.REACT_APP_API_URL}/users/following`, {
        headers: { Authorization: `Bearer ${activeToken}` }
      });
      if (res.ok) {
        const data = await res.json();
        setFollowedCreators(data);
      }
    } catch (err) {
      console.warn('Error fetching followed creators:', err);
    } finally {
      setFollowedCreatorsLoading(false);
    }
  }, [isAuthenticated]);

  useEffect(() => {
    if (showSaved && savedTab === 'following') {
      fetchFollowedCreators();
    }
  }, [showSaved, savedTab, fetchFollowedCreators]);

  // NEW: State to receive items from SmartEventList
  const [displayedItems, setDisplayedItems] = useState([]);

  // TTS functionality
  const { speak, stop, isSupported, availableVoices, selectedVoiceName, setVoice, audioOutputDevices, selectedOutputDeviceId, setOutputDevice } = useTTS(language);
  const [isTTSAutoEnabled, setIsTTSAutoEnabled] = useState(false);
  const previousChildClickedRef = useRef(childClicked);

  const menuConfig = menuItems(isAuthenticated, language);

  const handleMenuItemClick = async (item) => {
    if (item.action === 'logout') {
      await logout();
      navigate('/');
    } else if (item.action === 'add-data') {
      setCategoryModalOpen(true);
    } else if (item.external) {
      window.open(item.path, '_blank');
    } else {
      navigate(item.path);
    }
    setDrawerOpen(false);
  };

  const getMenuItemIcon = (label) => {
    const l = (label || '').toLowerCase();
    if (l.includes('scan') || l.includes('qr') || l.includes('扫描') || l.includes('สแกน')) {
      return <QrCodeScannerIcon />;
    }
    if (l.includes('tvent') || l.includes('旅游团') || l.includes('ทัวร์กลุ่ม')) {
      return <PeopleAltIcon />;
    }
    if (l.includes('event') || l.includes('กิจกรรม') || l.includes('活动') || l.includes('adding') || l.includes('添加') || l.includes('เพิ่ม')) {
      return <PlaylistAddIcon />;
    }
    if (l.includes('dashboard') || l.includes('แดชบอร์ด') || l.includes('仪表板')) {
      return <DashboardIcon />;
    }
    if (l.includes('login') || l.includes('เข้าสู่ระบบ') || l.includes('登录') || l.includes('sign up') || l.includes('注册') || l.includes('สมัครสมาชิก')) {
      return <LoginIcon />;
    }
    return null;
  };

  // NEW: Handle items updates from SmartEventList
  const handleItemsChange = useCallback((items) => {
    setDisplayedItems(items);
  }, []);

  // NEW: Handle map sync when active item changes in list
  // NOTE: Does NOT set childClicked to prevent feedback loop
  // (onActiveIndexChange already reports the index separately)
  const handleMapSync = useCallback((lat, lng, index) => {
    // Don't let list items far from the user's GPS location override the map center.
    // This prevents Bangkok items from pulling the map away from Hat Yai (or any
    // non-Bangkok location) when SmartEventList auto-fires on initial load.
    // 2 degrees ≈ 220 km — items further than that won't re-center the map.
    if (userLocation) {
      const dLat = Math.abs(lat - userLocation.lat);
      const dLng = Math.abs(lng - userLocation.lng);
      if (dLat > 2 || dLng > 2) return;
    }
    setCoordinates({ lat, lng });
  }, [setCoordinates, userLocation]);

  // Handler for when active index changes in the list (kept for compatibility)
  // STEP 6: Ignore active index changes while stabilizing
  const handleActiveIndexChange = useCallback((index, isStabilizing = false, isAuthoritative = false) => {
    if (isStabilizing) {
      // we are in the middle of a window shift → DO NOT update map, DO NOT set childClicked
      console.log('[DesktopLayout] (ignored) active index', index, 'because list is stabilizing');
      return;
    }

    // extra guard: sometimes right after shift we get index 0 from a half-baked window
    if (index === 0 && !isAuthoritative) {
      console.log('[DesktopLayout] (ignored) weak index 0 after shift');
      return;
    }

    const place = displayedItems[index];
    if (!place) return;

    // Guard: don't update if index hasn't changed (prevents feedback loops and unnecessary re-renders)
    if (childClicked === index) {
      // console.log('[DesktopLayout] Skipping childClicked update - already at index', index);
      return;
    }

    console.log('[DesktopLayout] Active index changed to', index, '- updating childClicked and map position');

    // select the event in the list
    setChildClicked(index);

    // move map to this event
    setCoordinates({
      lat: place.latitude,
      lng: place.longitude,
    });
  }, [displayedItems, childClicked, setChildClicked, setCoordinates]);

  // Get selected event ID from childClicked index
  const selectedEventId = childClicked !== null && displayedItems[childClicked]
    ? (displayedItems[childClicked].event_id || displayedItems[childClicked].id)
    : null;

  // TTS toggle handler
  const handleTTSToggle = useCallback(() => {
    if (isTTSAutoEnabled) {
      // Disable auto-read mode
      setIsTTSAutoEnabled(false);
      stop();
    } else {
      // Enable auto-read mode
      setIsTTSAutoEnabled(true);
      
      // Play a short silent utterance to unlock speech synthesis on iOS/Safari/macOS
      if (typeof window !== 'undefined' && window.speechSynthesis) {
        const unlockUtterance = new SpeechSynthesisUtterance('');
        window.speechSynthesis.speak(unlockUtterance);
      }

      // If no item is selected, automatically select the first one!
      let targetIndex = childClicked;
      if (targetIndex === null && displayedItems && displayedItems.length > 0) {
        targetIndex = 0;
        setChildClicked(0);
      }

      const focusedItem = displayedItems[targetIndex];
      if (focusedItem) {
        const text = formatEventForTTS(focusedItem, language);
        speak(text);
      }
    }
  }, [isTTSAutoEnabled, stop, speak, displayedItems, childClicked, language, setChildClicked]);

  const handleTTSRestart = useCallback(() => {
    if (!isTTSAutoEnabled) return;
    const idx = childClicked !== null ? childClicked : 0;
    const focusedItem = displayedItems[idx];
    if (focusedItem) {
      const text = formatEventForTTS(focusedItem, language);
      speak(text);
    }
  }, [isTTSAutoEnabled, displayedItems, childClicked, language, speak]);

  const previousLanguageRef = useRef(language);

  // Auto-read new middle item when scrolling or when language changes (if TTS is enabled)
  useEffect(() => {
    // Skip on initial mount or when childClicked becomes null
    if (previousChildClickedRef.current === null && childClicked === null) {
      return;
    }

    const languageChanged = previousLanguageRef.current !== language;
    previousLanguageRef.current = language;

    // Auto-read if TTS mode is enabled AND (childClicked changed OR language changed)
    if (isTTSAutoEnabled && (childClicked !== previousChildClickedRef.current || languageChanged)) {
      previousChildClickedRef.current = childClicked;

      // Stop current reading
      stop();

      // Start reading current item
      if (childClicked !== null && displayedItems[childClicked]) {
        const focusedItem = displayedItems[childClicked];
        const text = formatEventForTTS(focusedItem, language);
        speak(text);
      }
    } else {
      previousChildClickedRef.current = childClicked;
    }
  }, [childClicked, isTTSAutoEnabled, displayedItems, language, speak, stop]);

  const isTTSDisabled = !isSupported || !displayedItems || displayedItems.length === 0;

  return (
    <Box sx={{ 
      height: '100vh',
      width: '100vw',
      display: 'flex',
      flexDirection: 'row',
      backgroundColor: '#f6f7fb'
    }}>
      {/* Map Section - 60% width */}
      <Box sx={{
        width: '60%',
        height: '100vh',
        position: 'relative',
        overflow: 'hidden'
      }}>
        <Map
          setCoordinates={setCoordinates}
          setBounds={setBounds}
          bounds={bounds}
          coordinates={coordinates}
          places={displayedItems} // Use items from SmartEventList
          selectedEventId={selectedEventId} // Pass actual event ID, not index
          onMarkerClick={handleMarkerClick}
          userLocation={userLocation}
          onSearch={onSearch}
          disablePopups={false}
          onCreatorClick={onCreatorClick}
        />

        {/* Header overlay on map */}
        <Box sx={{
          position: 'absolute',
          top: 16,
          left: 16,
          zIndex: 1000,
          backgroundColor: 'rgba(255, 255, 255, 0.92)',
          borderRadius: '24px',
          boxShadow: '0 18px 42px rgba(31, 41, 55, 0.16)',
          border: '1px solid rgba(255,255,255,0.72)',
          backdropFilter: 'blur(16px)',
          maxWidth: 'calc(100% - 32px)'
        }}>
          <Header
            setCoordinates={setCoordinates}
            type={type}
            setType={setType}
            startDate={startDate}
            setStartDate={setStartDate}
            endDate={endDate}
            setEndDate={setEndDate}
            mapBounds={bounds}
            onSearch={onSearch}
            categoryFilter={uiCategoryFilter}
            setCategoryFilter={setUiCategoryFilter}
          />

        {/* Hamburger menu removed for desktop view only */}

        {/* Exclamation mark moved to Header component next to language flags */}
        </Box>

      </Box>

      {/* Right Sidebar - expanded width, accounting for vertical menu */}
      <Box
        sx={{
          width: 'calc(40% - 75px)', // Further reduced margin for vertical menu
          height: '100vh',
          display: 'flex',
          flexDirection: 'column',
          marginRight: '75px', // Further reduced space for the vertical menu
          backgroundColor: '#ffffff',
          boxShadow: '-14px 0 34px rgba(31, 41, 55, 0.08)',
          zIndex: 2
        }}
        onWheel={() => setFollowList(true)}
        onTouchStart={() => setFollowList(true)}
      >
        {/* Saved panel — mounted only when first opened, hidden when not active */}
        {savedEverOpened && (
          <Box sx={{ height: '100%', display: showSaved ? 'flex' : 'none', flexDirection: 'column' }}>
            <Box sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              px: 2,
              py: 1.5,
              borderBottom: '1px solid #f0f0f0',
              flexShrink: 0
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <IconButton size="small" onClick={() => setShowSaved(false)}>
                  <ArrowBackIcon />
                </IconButton>
                <Typography variant="h6" sx={{ fontWeight: 800, color: 'text.primary', fontSize: '1.1rem' }}>
                  {language === 'TH' ? 'ที่บันทึกไว้' : language === 'CN' ? '已保存' : 'Saved'}
                </Typography>
              </Box>

              {/* Segmented Control Selector Tabs */}
              <Box sx={{
                display: 'flex',
                backgroundColor: '#f1f5f9',
                borderRadius: '20px',
                p: 0.5,
                border: '1px solid #e2e8f0'
              }}>
                <Button
                  size="small"
                  onClick={() => setSavedTab('items')}
                  sx={{
                    borderRadius: '16px',
                    fontSize: '0.78rem',
                    fontWeight: 750,
                    textTransform: 'none',
                    py: 0.4,
                    px: 2,
                    color: savedTab === 'items' ? '#ffffff' : '#475569',
                    backgroundColor: savedTab === 'items' ? '#7c3aed' : 'transparent',
                    boxShadow: savedTab === 'items' ? '0 2px 8px rgba(124, 58, 237, 0.25)' : 'none',
                    '&:hover': {
                      backgroundColor: savedTab === 'items' ? '#6d28d9' : 'rgba(0,0,0,0.04)'
                    },
                    transition: 'all 0.2s'
                  }}
                >
                  {language === 'TH' ? 'รายการ' : language === 'CN' ? '项目' : 'Items'}
                </Button>
                <Button
                  size="small"
                  onClick={() => setSavedTab('following')}
                  sx={{
                    borderRadius: '16px',
                    fontSize: '0.78rem',
                    fontWeight: 750,
                    textTransform: 'none',
                    py: 0.4,
                    px: 2,
                    color: savedTab === 'following' ? '#ffffff' : '#475569',
                    backgroundColor: savedTab === 'following' ? '#7c3aed' : 'transparent',
                    boxShadow: savedTab === 'following' ? '0 2px 8px rgba(124, 58, 237, 0.25)' : 'none',
                    '&:hover': {
                      backgroundColor: savedTab === 'following' ? '#6d28d9' : 'rgba(0,0,0,0.04)'
                    },
                    transition: 'all 0.2s'
                  }}
                >
                  {language === 'TH' ? 'ติดตาม' : language === 'CN' ? '关注' : 'Following'}
                </Button>
              </Box>
            </Box>

            <Box sx={{ flex: 1, overflowY: 'auto' }}>
              {savedTab === 'items' ? (
                <SavedItemsList navigate={navigate} language={language} refreshKey={savedRefreshKey} />
              ) : (
                followedCreatorsLoading ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', p: 3 }}>
                    <Typography sx={{ color: 'text.secondary', fontSize: '0.88rem' }}>
                      {language === 'TH' ? 'กำลังโหลด...' : language === 'CN' ? '正在加载...' : 'Loading...'}
                    </Typography>
                  </Box>
                ) : followedCreators.length === 0 ? (
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', p: 4, textAlign: 'center' }}>
                    <Typography sx={{ fontSize: '2.5rem', mb: 1 }}>👥</Typography>
                    <Typography sx={{ color: 'text.secondary', fontWeight: 600, fontSize: '0.9rem' }}>
                      {language === 'TH' ? 'ยังไม่ได้ติดตามใคร' : language === 'CN' ? '尚未关注任何人' : 'Not following anyone yet'}
                    </Typography>
                  </Box>
                ) : (
                  <Box sx={{ p: 1.5 }}>
                    {followedCreators.map((creator) => {
                      const creatorInitials = creator.name ? creator.name.substring(0, 2).toUpperCase() : 'US';
                      return (
                        <Box 
                          key={creator.id}
                          onClick={() => {
                            if (onCreatorClick) {
                              onCreatorClick(creator);
                              setShowSaved(false);
                            }
                          }}
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1.5,
                            p: 1.5,
                            mb: 1.2,
                            borderRadius: '16px',
                            backgroundColor: '#ffffff',
                            border: '1px solid #f1f5f9',
                            boxShadow: '0 2px 8px rgba(0,0,0,0.02)',
                            cursor: 'pointer',
                            transition: 'all 0.2s',
                            '&:hover': {
                              transform: 'translateY(-1px)',
                              boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
                              borderColor: '#e2e8f0'
                            }
                          }}
                        >
                          {/* Avatar */}
                          <Box sx={{
                            width: 44,
                            height: 44,
                            borderRadius: '50%',
                            backgroundColor: '#7c3aed',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: '#fff',
                            fontWeight: 800,
                            fontSize: 16,
                            boxShadow: '0 2px 6px rgba(124, 58, 237, 0.2)',
                            overflow: 'hidden',
                            flexShrink: 0
                          }}>
                            {creator.avatar_url ? (
                              <img 
                                src={creator.avatar_url} 
                                alt={creator.name}
                                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                              />
                            ) : (
                              creatorInitials
                            )}
                          </Box>

                          {/* Detail */}
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
                              <Typography sx={{ fontWeight: 800, color: 'text.primary', fontSize: '0.92rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                {creator.name}
                              </Typography>
                              {creator.role && (
                                <Box sx={{
                                  px: 0.8,
                                  py: 0.1,
                                  borderRadius: '10px',
                                  backgroundColor: creator.role === 'artist' ? 'rgba(124, 58, 237, 0.08)' : 'rgba(13, 148, 136, 0.08)',
                                  border: creator.role === 'artist' ? '1px solid rgba(124, 58, 237, 0.15)' : '1px solid rgba(13, 148, 136, 0.15)'
                                }}>
                                  <Typography sx={{
                                    fontSize: '0.6rem',
                                    fontWeight: 700,
                                    color: creator.role === 'artist' ? '#7c3aed' : '#0d9488',
                                    textTransform: 'uppercase'
                                  }}>
                                    {creator.role}
                                  </Typography>
                                </Box>
                              )}
                            </Box>
                            {creator.bio && (
                              <Typography sx={{ fontSize: '0.78rem', color: 'text.secondary', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', mt: 0.2 }}>
                                {creator.bio}
                              </Typography>
                            )}
                            <Typography sx={{ fontSize: '0.75rem', color: '#7c3aed', fontWeight: 650, mt: 0.3 }}>
                              📍 {creator.event_count || 0} {language === 'TH' ? 'รายการน่าสนใจ' : language === 'CN' ? '个推荐项目' : 'items'}
                            </Typography>
                          </Box>

                          {/* Action / Unfollow Button */}
                          <Button
                            variant="outlined"
                            size="small"
                            onClick={async (e) => {
                              e.stopPropagation(); // prevent clicking card to open creator details
                              try {
                                const activeToken = TokenService.getAccessToken();
                                const res = await fetch(`${process.env.REACT_APP_API_URL}/users/${creator.id}/toggle-follow`, {
                                  method: 'POST',
                                  headers: { Authorization: `Bearer ${activeToken}` }
                                });
                                if (res.ok) {
                                  // Refresh list
                                  fetchFollowedCreators();
                                }
                              } catch (err) {
                                console.error('Error toggling follow from list:', err);
                              }
                            }}
                            sx={{
                              borderRadius: '20px',
                              fontSize: '0.68rem',
                              fontWeight: 700,
                              py: 0.3,
                              px: 1.5,
                              color: '#7c3aed',
                              borderColor: '#7c3aed',
                              textTransform: 'none',
                              flexShrink: 0,
                              '&:hover': {
                                backgroundColor: 'rgba(124, 58, 237, 0.04)',
                                borderColor: '#6d28d9',
                                color: '#6d28d9'
                              }
                            }}
                          >
                            {language === 'TH' ? 'ติดตามแล้ว' : language === 'CN' ? '已关注' : 'Following'}
                          </Button>
                        </Box>
                      );
                    })}
                  </Box>
                )
              )}
            </Box>
          </Box>
        )}
        {/* RightSidebar — always mounted, hidden behind saved panel to preserve scroll/list state */}
        <Box sx={{ display: showSaved ? 'none' : 'flex', flexDirection: 'column', height: '100%', flex: 1 }}>
          <RightSidebar
            childClicked={childClicked}
            onLocationClick={handleLocationClick}
            type={type}
            setType={setType}
            language={language}
            searchQuery={searchQuery}
            startDate={startDate}
            endDate={endDate}
            source={source}
            excludeCategories={excludeCategories}
            categoryFilter={categoryFilter}
            onActiveIndexChange={handleActiveIndexChange}
            onItemsChange={handleItemsChange}
            onMapSyncNeeded={handleMapSync}
            userLocation={userLocation}
            feedLat={feedLat}
            feedLng={feedLng}
            feedRadiusKm={feedRadiusKm}
            selectedCreator={selectedCreator}
            setSelectedCreator={setSelectedCreator}
            creatorProfile={creatorProfile}
          />
        </Box>
      </Box>

      {/* Vertical Bottom Menu - positioned at right edge */}
      <Box sx={{ 
        position: 'fixed',
        right: 0,
        top: '50%',
        transform: 'translateY(-50%)',
        zIndex: 1000,
        backgroundColor: 'rgba(255,255,255,0.96)',
        borderLeft: '1px solid rgba(229,231,235,0.9)',
        borderRadius: '18px 0 0 18px',
        boxShadow: '-10px 0 28px rgba(31, 41, 55, 0.12)',
        backdropFilter: 'blur(14px)'
      }}>
        <Box sx={{ 
          display: 'flex',
          flexDirection: 'column'
        }}>
          <BottomMenu onShowSaved={() => { setShowSaved(true); setSavedEverOpened(true); setSavedRefreshKey((k) => k + 1); }} savedActive={showSaved} />
        </Box>
      </Box>
      {/* Global Hamburger Drawer Menu */}
      <Drawer
        anchor="left"
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
      >
        <Box
          sx={{ width: 250 }}
          role="presentation"
          onClick={() => setDrawerOpen(false)}
          onKeyDown={() => setDrawerOpen(false)}
        >
          <List>
            {menuConfig.map((item, index) => {
              const icon = getMenuItemIcon(item.label);
              return (
                <ListItem key={index} button onClick={() => handleMenuItemClick(item)}>
                  {icon && (
                    <ListItemIcon>
                      {icon}
                    </ListItemIcon>
                  )}
                  <ListItemText primary={item.label} />
                </ListItem>
              );
            })}
          </List>
        </Box>
      </Drawer>

      {/* Category Selection Modal */}
      <CategorySelectionModal
        open={categoryModalOpen}
        onClose={() => setCategoryModalOpen(false)}
        language={language}
      />

      {/* Exclamation mark menu functionality removed - now just an info button in header */}
    </Box>
  );
};

export default DesktopLayout;
